import React, { useState, useEffect } from 'react';
import { Table, Typography, message, Tag, Spin, Button, Modal, Form, Input, Switch, Space, Select, Tooltip } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SendOutlined } from '@ant-design/icons';
import moment from 'moment';
import { notificationAPI } from '../services/api';

const { Title } = Typography;
const { Option } = Select;

const Webhooks = () => {
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingWebhook, setEditingWebhook] = useState(null);
  const [deletingWebhook, setDeletingWebhook] = useState(null);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [form] = Form.useForm();

  useEffect(() => {
    fetchWebhooks();
  }, []);

  useEffect(() => {
    fetchWebhooks();
  }, [pagination.current, pagination.pageSize]);

  const fetchWebhooks = async () => {
    try {
      const response = await notificationAPI.getWebhooks();
      const allData = response.data || [];
      const total = allData.length;

      // 前端分页计算
      const startIndex = (pagination.current - 1) * pagination.pageSize;
      const endIndex = startIndex + pagination.pageSize;
      const currentPageData = allData.slice(startIndex, endIndex);

      setWebhooks(currentPageData);
      setPagination((prev) => ({ ...prev, total }));
    } catch (error) {
      message.error('获取webhook失败');
      console.error('获取webhook失败:', error);
      setWebhooks([]);
      setPagination((prev) => ({ ...prev, total: 0 }));
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (paginationConfig) => {
    setPagination({
      current: paginationConfig.current,
      pageSize: paginationConfig.pageSize,
      total: paginationConfig.total,
    });
  };

  const handleCreate = () => {
    setEditingWebhook(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingWebhook(record);
    form.setFieldsValue({
      name: record.name,
      type: record.type,
      webhook_url: record.webhook_url,
      enabled: record.enabled,
      custom_keywords: record.custom_keywords || [],
      sign_content: record.sign_content,
    });
    setIsModalVisible(true);
  };

  const handleDelete = (record) => {
    setDeletingWebhook(record);
    setIsDeleteModalVisible(true);
  };

  const handleTest = async (record) => {
    console.log('Testing webhook', record.id);
    try {
      const res = await notificationAPI.testWebhook(record.id);
      message.success('测试通知已发送');
      Modal.info({
        title: '测通返回',
        width: 700,
        content: <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>{JSON.stringify(res?.data ?? {}, null, 2)}</pre>,
      });
    } catch (error) {
      message.error('测试通知发送失败');
      console.error('测试通知发送失败:', error);
      const detail = error?.response?.data?.detail || error?.response?.data || error?.message || '未知错误';
      Modal.error({
        title: '测通失败',
        width: 700,
        content: <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>{typeof detail === 'string' ? detail : JSON.stringify(detail, null, 2)}</pre>,
      });
    }
  };

  const handleConfirmDelete = async () => {
    if (!deletingWebhook) return;

    try {
      await notificationAPI.deleteWebhook(deletingWebhook.id);
      message.success('webhook删除成功');
      setIsDeleteModalVisible(false);
      setDeletingWebhook(null);
      fetchWebhooks();
    } catch (error) {
      message.error('删除webhook失败');
      console.error('删除webhook失败:', error);
    }
  };

  const handleCancelDelete = () => {
    setIsDeleteModalVisible(false);
    setDeletingWebhook(null);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();

      if (editingWebhook) {
        // 更新webhook
        await notificationAPI.updateWebhook(editingWebhook.id, values);
        message.success('webhook更新成功');
      } else {
        // 创建webhook
        await notificationAPI.createWebhook(values);
        message.success('webhook创建成功');
      }

      setIsModalVisible(false);
      form.resetFields();
      fetchWebhooks();
    } catch (error) {
      message.error(error.response?.data?.detail || '操作失败');
      console.error('操作失败:', error);
    }
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '名称',
      dataIndex: 'name',
      key: 'name',
      width: 160,
    },
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      width: 120,
      render: (type) => (type === 'discord' ? 'Discord' : 'DingTalk'),
    },
    {
      title: 'Webhook URL',
      dataIndex: 'webhook_url',
      key: 'webhook_url',
      width: 300,
      ellipsis: {
        showTitle: false,
      },
      render: (text) => (
        <Tooltip placement="topLeft" title={text}>
          {text}
        </Tooltip>
      ),
    },
    {
      title: '自定义关键字',
      dataIndex: 'custom_keywords',
      key: 'custom_keywords',
      width: 220,
      render: (keywords) => {
        if (!keywords || keywords.length === 0) return '-';
        return (
          <Tooltip placement="topLeft" title={keywords.join(', ')}>
            {keywords.join(', ')}
          </Tooltip>
        );
      },
    },
    {
      title: '签名内容',
      dataIndex: 'sign_content',
      key: 'sign_content',
      width: 100,
      render: (v) => (v ? '已配置' : '-'),
    },
    {
      title: '状态',
      dataIndex: 'enabled',
      key: 'enabled',
      render: (enabled) => <Tag color={enabled ? 'green' : 'red'}>{enabled ? '启用' : '禁用'}</Tag>,
      width: 100,
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (date) => moment.utc(date).utcOffset(8).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: '操作',
      key: 'action',
      width: 300,
      fixed: 'right',
      render: (_, record) => (
        <Space size="middle">
          <Button type="link" icon={<SendOutlined />} onClick={() => handleTest(record)}>
            测通
          </Button>
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
            编辑
          </Button>
          <Button type="link" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record)}>
            删除
          </Button>
        </Space>
      ),
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <Title level={3}>Webhook管理</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
          新建Webhook
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={webhooks}
        rowKey="id"
        onChange={handleTableChange}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `显示 ${range[0]}-${range[1]} 条，共 ${total} 条`,
        }}
      />

      <Modal title={editingWebhook ? '编辑Webhook' : '新建Webhook'} open={isModalVisible} onOk={handleModalOk} onCancel={handleModalCancel}>
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="名称" rules={[{ required: true, message: '请输入名称' }]}>
            <Input placeholder="输入webhook的唯一名称" />
          </Form.Item>

          <Form.Item name="type" label="类型" rules={[{ required: true, message: '请选择类型' }]}>
            <Select placeholder="请选择webhook类型">
              <Option value="discord">Discord</Option>
              <Option value="dingtalk">DingTalk</Option>
            </Select>
          </Form.Item>

          <Form.Item name="webhook_url" label="Webhook URL" rules={[{ required: true, message: '请输入Webhook URL' }]}>
            <Input placeholder="输入webhook的URL" />
          </Form.Item>

          <Form.Item name="custom_keywords" label="自定义关键字">
            <Select mode="tags" placeholder="输入关键字后回车，可多个（最多10个）" maxTagCount={10} />
          </Form.Item>

          <Form.Item name="sign_content" label="签名内容">
            <Input placeholder="可选：填写签名内容/密钥（如DingTalk SEC...）" />
          </Form.Item>

          <Form.Item name="enabled" label="启用状态" valuePropName="checked" initialValue={true}>
            <Switch checkedChildren="启用" unCheckedChildren="禁用" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal title="确认删除" open={isDeleteModalVisible} onOk={handleConfirmDelete} onCancel={handleCancelDelete} okText="确认删除" cancelText="取消" okType="danger">
        <p>确定要删除webhook "{deletingWebhook?.name}" 吗？</p>
        <p style={{ color: 'red', marginTop: '8px' }}>此操作无法撤销，删除后将永久失去该webhook。</p>
      </Modal>
    </div>
  );
};

export default Webhooks;
